import React, { useState, useRef, useEffect } from "react";
import InputField from "components/fields/InputField";
import Checkbox from "components/checkbox";
import { useAuthContext } from "../../hooks/AuthContext";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";

export default function SignUp() {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const { register, loading, error } = useAuthContext();
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    gsap.from(card, {
      opacity: 0,
      y: 50,
      duration: 0.8,
      ease: "power3.out",
    });

    const fields = gsap.utils.toArray(".form-field");
    gsap.from(fields, {
      opacity: 0,
      y: 30,
      duration: 0.6,
      stagger: 0.1,
      ease: "power2.out",
      delay: 0.3,
    });
  }, []);

  const handleSubmit = async () => {
    try {
      await register(
        firstname,
        lastname,
        email,
        phone,
        dob,
        password,
        passwordConfirmation
      );
      navigate("/admin");
    } catch (err) {
      // error handled by hook
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center p-4 font-poppins">
      <div
        ref={cardRef}
        className="shadow-black/5 w-full max-w-xl rounded-2xl bg-white p-8 shadow-xl dark:border dark:border-stone-700/50 dark:bg-stone-800/60 dark:shadow-2xl dark:backdrop-blur-lg md:p-12"
      >
        <h4 className="mb-4 text-center font-serif text-3xl font-bold text-gray-800 dark:text-white md:text-4xl">
          Join Our Community
        </h4>
        <p className="mb-8 text-center text-base text-gray-500 dark:text-gray-400">
          Create your account to get started!
        </p>

        <div className="mb-6 flex items-center gap-3">
          <div className="h-px w-full bg-gray-200 dark:bg-stone-700" />
          <p className="text-base text-gray-500 dark:text-gray-400">
            {" "}
            Register{" "}
          </p>
          <div className="h-px w-full bg-gray-200 dark:bg-stone-700" />
        </div>

        <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
          {/* First Name */}
          <div className="form-field">
            <InputField
              variant="auth"
              extra="mb-4"
              label="First Name*"
              placeholder="Your first name"
              id="firstname"
              type="text"
              value={firstname}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFirstname(e.target.value)
              }
            />
          </div>

          {/* Last Name */}
          <div className="form-field">
            <InputField
              variant="auth"
              extra="mb-4"
              label="Last Name*"
              placeholder="Your last name"
              id="lastname"
              type="text"
              value={lastname}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLastname(e.target.value)
              }
            />
          </div>
        </div>

        {/* Email */}
        <div className="form-field">
          <InputField
            variant="auth"
            extra="mb-4"
            label="Email*"
            placeholder="your@email.com"
            id="email"
            type="text"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
          />
        </div>

        <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
          {/* Password */}
          <div className="form-field">
            <InputField
              variant="auth"
              extra="mb-4"
              label="Password*"
              placeholder="Min. 8 characters"
              id="password"
              type="password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
            />
          </div>

          {/* Password Confirmation */}
          <div className="form-field">
            <InputField
              variant="auth"
              extra="mb-4"
              label="Confirm Password*"
              placeholder="Confirm your password"
              id="password_confirmation"
              type="password"
              value={passwordConfirmation}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPasswordConfirmation(e.target.value)
              }
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
          {/* Phone */}
          <div className="form-field">
            <InputField
              variant="auth"
              extra="mb-4"
              label="Phone*"
              placeholder="e.g. +1 555 555 5555"
              id="phone"
              type="text"
              value={phone}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPhone(e.target.value)
              }
            />
          </div>

          {/* Date of Birth */}
          <div className="form-field">
            <InputField
              variant="auth"
              extra="mb-4"
              label="Date of Birth*"
              placeholder="YYYY-MM-DD"
              id="dob"
              type="date"
              value={dob}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDob(e.target.value)
              }
            />
          </div>
        </div>

        {/* Checkbox */}
        <div className="form-field mb-4 flex items-center justify-between px-2">
          <div className="flex items-center">
            <Checkbox
              checked={agreeTerms}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAgreeTerms(e.target.checked)
              }
            />
            <p className="ml-2 text-sm font-medium text-gray-600 dark:text-stone-300">
              I agree to the{" "}
              <a
                href="#"
                className="font-semibold text-nature-green hover:text-nature-green-dark dark:text-nature-green-light dark:hover:text-nature-green"
              >
                Terms and Conditions
              </a>
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 text-center text-sm text-red-500">{error}</div>
        )}

        <div className="form-field">
          <button
            onClick={handleSubmit}
            className="linear mt-2 w-full rounded-full border border-transparent bg-nature-green py-3 text-base font-bold text-white shadow-lg shadow-nature-green/20 transition duration-200 hover:bg-nature-green-dark active:bg-nature-green-dark/80 disabled:cursor-not-allowed disabled:bg-gray-300 dark:bg-nature-green dark:text-white dark:shadow-nature-green/30 dark:hover:bg-nature-green/90 dark:active:bg-nature-green/80"
            disabled={loading || !agreeTerms}
            title={
              !agreeTerms
                ? "Please agree to the Terms and Conditions to sign up."
                : ""
            }
          >
            {loading ? "Creating Account..." : "Sign Up"}
          </button>
        </div>

        <div className="mt-6 text-center">
          <span className="text-sm font-medium text-gray-500 dark:text-stone-400">
            Already have an account?
          </span>
          <button
            className="ml-1 text-sm font-medium text-nature-green hover:text-nature-green-dark dark:text-nature-green-light dark:hover:text-nature-green"
            onClick={() => navigate("/auth/sign-in")}
          >
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
}
